import { Link } from 'react-router';
import { useConsulta } from '@plataforma/web/api';
import { mxn } from '../formato';
import { nombreVehiculo } from './Tablero';
import type { Vehiculo } from './tipos';

interface Rentabilidad {
  vehiculoId: string;
  horas: string;
  margenEsperadoMxn: string | null;
  costoTotalMxn: string | null;
  valorEsperadoVentaMxn: string | null;
  gananciaPorHoraMxn: string | null;
}

/**
 * Rentabilidad por hora (arquitectura 2.15, sección 6): ganancia por hora = margen ÷ horas de
 * los relojes del Taller. Para comparar autos y caminos: un margen grande con muchas horas puede
 * rendir menos que uno chico y rápido.
 */
export function Rentabilidad() {
  const lista = useConsulta<Rentabilidad[]>('/finance/rentabilidad');
  const { datos: vehiculos } = useConsulta<Vehiculo[]>('/inventory/vehiculos');
  const nombre = (id: string) => {
    const v = vehiculos?.find((x) => x.id === id);
    return v ? `${nombreVehiculo(v)} · ${v.vin.slice(-6)}` : id.slice(-8);
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Rentabilidad por hora</h1>
          <div className="subtitulo">
            Margen esperado entre las horas de taller registradas en cada auto
          </div>
        </div>
      </div>
      <section className="seccion">
        {lista.error && <p className="error">{lista.error}</p>}
        {lista.datos && !lista.datos.length ? (
          <p className="vacio">
            Sin horas registradas todavía: se registran al cerrar órdenes del Taller con reloj.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Vehículo</th>
                <th className="num">Horas</th>
                <th className="num">Costo total</th>
                <th className="num">Margen esperado</th>
                <th className="num">Ganancia por hora</th>
              </tr>
            </thead>
            <tbody>
              {(lista.datos ?? []).map((r) => (
                <tr key={r.vehiculoId}>
                  <td>
                    <Link to={`/vehiculos/${r.vehiculoId}`}>{nombre(r.vehiculoId)}</Link>
                  </td>
                  <td className="num">
                    {Number(r.horas).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="num">{r.costoTotalMxn ? mxn(r.costoTotalMxn) : '—'}</td>
                  <td
                    className="num"
                    style={{
                      color: r.margenEsperadoMxn?.startsWith('-') ? 'var(--peligro)' : undefined,
                    }}
                  >
                    {r.margenEsperadoMxn ? mxn(r.margenEsperadoMxn) : '—'}
                  </td>
                  <td className="num">
                    <strong>{r.gananciaPorHoraMxn ? mxn(r.gananciaPorHoraMxn) : '—'}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
